import { create, useStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { StrikeWeightDesignMode, type StrikeWeightDesignTarget } from '@/lib/piano/touch-design/design/strike-weight-design';
import { FrontWeightDesignMode, type FrontWeightDesignTarget } from '@/lib/piano/touch-design/design/front-weight-design';
import { StrikeWeightRatioDesignMode, type StrikeWeightRatioDesignTarget } from '@/lib/piano/touch-design/design/strike-weight-ratio-design';
import { WippenSupportSpringsDesignMode, type WippenSupportSpringsDesignTarget, type WippenTensionDesign } from '@/lib/piano/touch-design/design/wippen-support-springs-design';
import { SmoothStrategy } from '@/lib/geometry/curve-smoother/smooth-strategy.enum';
import { StrikeWeightLevel } from '@/lib/piano/touch-design/hammer-weight-level';
export interface DesignStoreState {
  frontWeightDesignMode: FrontWeightDesignMode | null;
  frontWeightDesignStandardTarget: FrontWeightDesignTarget | null;
  frontWeightDesignComputedBalanceWeightTarget: number | null;
  frontWeightDesignTarget: FrontWeightDesignTarget | null;
  strikeWeightDesignMode: StrikeWeightDesignMode | null;
  strikeWeightDesignTarget: StrikeWeightDesignTarget | null;
  strikeWeightDesignComputedBalanceWeightTarget: number | null;
  strikeWeightDesignLatestSmoothTarget: StrikeWeightDesignTarget | null;
  strikeWeightDesignLatestStandarTarget: StrikeWeightDesignTarget | null;
  strikeWeightRatioDesignMode: StrikeWeightRatioDesignMode | null;
  strikeWeightRatioDesignLatestFixedTarget: StrikeWeightRatioDesignTarget | null;
  strikeWeightRatioDesignLatestSmoothTarget: StrikeWeightRatioDesignTarget | null;
  strikeWeightRatioDesignTarget: StrikeWeightRatioDesignTarget | null;
  wippenSupportSpringsDesignLatestSpringBalanceWeight: number | null;
  wippenSupportSpringsDesignLatestNumberOfSprings: number | null;
  wippenSupportSpringsDesignLatestTensionDropIndex: number | null;
  wippenSupportSpringsDesignMode: WippenSupportSpringsDesignMode | null;
  wippenSupportSpringsDesignTarget: WippenSupportSpringsDesignTarget;
}
interface VersionedDesignStoreState extends DesignStoreState {
  version: number;
}
interface DesignStoreActions {
  reset: () => void;
  updateFrontWeightDesign: (mode: FrontWeightDesignMode | null, target: FrontWeightDesignTarget | null) => void;
  updateStrikeWeightDesign: (mode: StrikeWeightDesignMode | null, target: StrikeWeightDesignTarget | null) => void;
  updateStrikeWeightRatioDesign: (mode: StrikeWeightRatioDesignMode | null, target: StrikeWeightRatioDesignTarget | null) => void;
  updateWippenSupportSpringsDesign: (mode: WippenSupportSpringsDesignMode | null, target: WippenSupportSpringsDesignTarget) => void;
}
export type DesignStore = VersionedDesignStoreState & DesignStoreActions;
const defaultDesignStoreValues: DesignStoreState = {
  frontWeightDesignComputedBalanceWeightTarget: 38,
  frontWeightDesignMode: FrontWeightDesignMode.AsMeasured,
  frontWeightDesignStandardTarget: 7,
  frontWeightDesignTarget: null,
  strikeWeightDesignComputedBalanceWeightTarget: 38,
  strikeWeightDesignLatestSmoothTarget: SmoothStrategy.LeastSquaresRegression,
  strikeWeightDesignLatestStandarTarget: StrikeWeightLevel.Level7,
  strikeWeightDesignMode: StrikeWeightDesignMode.AsMeasured,
  strikeWeightDesignTarget: null,
  strikeWeightRatioDesignLatestFixedTarget: 5.5,
  strikeWeightRatioDesignLatestSmoothTarget: SmoothStrategy.Median,
  strikeWeightRatioDesignMode: StrikeWeightRatioDesignMode.AsMeasured,
  strikeWeightRatioDesignTarget: null,
  wippenSupportSpringsDesignLatestNumberOfSprings: 60,
  wippenSupportSpringsDesignLatestSpringBalanceWeight: 9,
  wippenSupportSpringsDesignLatestTensionDropIndex: 20,
  wippenSupportSpringsDesignMode: WippenSupportSpringsDesignMode.None,
  wippenSupportSpringsDesignTarget: null
};
const createDesignStore = () => create<DesignStore>()(persist(set => ({
  ...defaultDesignStoreValues,
  reset: () => set(() => defaultDesignStoreValues),
  updateFrontWeightDesign: (mode: FrontWeightDesignMode | null, target: FrontWeightDesignTarget | null) => set(() => {
    const newState: Partial<DesignStore> = {
      frontWeightDesignMode: mode,
      frontWeightDesignTarget: target
    };
    if (mode === FrontWeightDesignMode.StandardCurves && target !== null) {
      newState.frontWeightDesignStandardTarget = target;
    }
    if (mode === FrontWeightDesignMode.Computed && target !== null) {
      newState.frontWeightDesignComputedBalanceWeightTarget = target;
    }
    return newState;
  }),
  updateStrikeWeightDesign: (mode: StrikeWeightDesignMode | null, target: StrikeWeightDesignTarget | null) => set(() => {
    const newState: Partial<DesignStore> = {
      strikeWeightDesignMode: mode,
      strikeWeightDesignTarget: target
    };
    if (mode === StrikeWeightDesignMode.StandardCurves && target !== null) {
      newState.strikeWeightDesignLatestStandarTarget = target;
    }
    if (mode === StrikeWeightDesignMode.Smoothed && target !== null) {
      newState.strikeWeightDesignLatestSmoothTarget = target;
    }
    if (mode === StrikeWeightDesignMode.Computed && target !== null && !isNaN(Number(target))) {
      newState.strikeWeightDesignComputedBalanceWeightTarget = Number(target);
    }
    return newState;
  }),
  updateStrikeWeightRatioDesign: (mode: StrikeWeightRatioDesignMode | null, target: StrikeWeightRatioDesignTarget | null) => set(() => {
    const newState: Partial<DesignStore> = {
      strikeWeightRatioDesignMode: mode,
      strikeWeightRatioDesignTarget: target
    };
    if (mode === StrikeWeightRatioDesignMode.Smoothed && target !== null) {
      newState.strikeWeightRatioDesignLatestSmoothTarget = target;
    }
    if (mode === StrikeWeightRatioDesignMode.FixedValue && target !== null) {
      newState.strikeWeightRatioDesignLatestFixedTarget = target;
    }
    return newState;
  }),
  updateWippenSupportSpringsDesign: (mode: WippenSupportSpringsDesignMode | null, target: WippenSupportSpringsDesignTarget) => set(() => {
    const newState: Partial<DesignStore> = {
      wippenSupportSpringsDesignMode: mode,
      wippenSupportSpringsDesignTarget: target
    };
    const isConstantOrSmoothTransitionMode = [WippenSupportSpringsDesignMode.Constant, WippenSupportSpringsDesignMode.SmoothTransition].includes(mode!);
    const tensionDesign = target as WippenTensionDesign;
    if (isConstantOrSmoothTransitionMode && tensionDesign?.numberOfSprings) {
      newState.wippenSupportSpringsDesignLatestNumberOfSprings = tensionDesign.numberOfSprings;
    }
    if (isConstantOrSmoothTransitionMode && tensionDesign?.springBalanceWeight) {
      newState.wippenSupportSpringsDesignLatestSpringBalanceWeight = tensionDesign.springBalanceWeight;
    }
    if (mode === WippenSupportSpringsDesignMode.SmoothTransition && tensionDesign?.tensionDropIndex) {
      newState.wippenSupportSpringsDesignLatestTensionDropIndex = tensionDesign.tensionDropIndex;
    }
    return newState;
  }),
  version: 1,
  wippenSupportSpringsDesignLatestNumberOfSprings: 60,
  wippenSupportSpringsDesignLatestSpringBalanceWeight: 9,
  wippenSupportSpringsDesignLatestTensionDropIndex: 20,
  wippenSupportSpringsDesignMode: WippenSupportSpringsDesignMode.None,
  wippenSupportSpringsDesignTarget: null
}), {
  name: `ptl.design`,
  partialize: (state: DesignStore): VersionedDesignStoreState => ({
    frontWeightDesignComputedBalanceWeightTarget: state.frontWeightDesignComputedBalanceWeightTarget,
    frontWeightDesignMode: state.frontWeightDesignMode,
    frontWeightDesignStandardTarget: state.frontWeightDesignStandardTarget,
    frontWeightDesignTarget: state.frontWeightDesignTarget,
    strikeWeightDesignComputedBalanceWeightTarget: state.strikeWeightDesignComputedBalanceWeightTarget,
    strikeWeightDesignLatestSmoothTarget: state.strikeWeightDesignLatestSmoothTarget,
    strikeWeightDesignLatestStandarTarget: state.strikeWeightDesignLatestStandarTarget,
    strikeWeightDesignMode: state.strikeWeightDesignMode,
    strikeWeightDesignTarget: state.strikeWeightDesignTarget,
    strikeWeightRatioDesignLatestFixedTarget: state.strikeWeightRatioDesignLatestFixedTarget,
    strikeWeightRatioDesignLatestSmoothTarget: state.strikeWeightRatioDesignLatestSmoothTarget,
    strikeWeightRatioDesignMode: state.strikeWeightRatioDesignMode,
    strikeWeightRatioDesignTarget: state.strikeWeightRatioDesignTarget,
    version: state.version,
    wippenSupportSpringsDesignLatestNumberOfSprings: state.wippenSupportSpringsDesignLatestNumberOfSprings,
    wippenSupportSpringsDesignLatestSpringBalanceWeight: state.wippenSupportSpringsDesignLatestSpringBalanceWeight,
    wippenSupportSpringsDesignLatestTensionDropIndex: state.wippenSupportSpringsDesignLatestTensionDropIndex,
    wippenSupportSpringsDesignMode: state.wippenSupportSpringsDesignMode,
    wippenSupportSpringsDesignTarget: state.wippenSupportSpringsDesignTarget
  })
}));
type DesignBoundStore = ReturnType<typeof createDesignStore>;
let designStore: DesignBoundStore | undefined = undefined;
export const useDesignStore = (): DesignBoundStore => {
  designStore ??= createDesignStore();
  return designStore;
};
export const useFrontWeightDesign = () => {
  return useStore(useDesignStore(), useShallow(_temp));
};
export const useStrikeWeightDesign = () => {
  return useStore(useDesignStore(), useShallow(_temp2));
};
export const useStrikeWeightRatioDesign = () => {
  return useStore(useDesignStore(), useShallow(_temp3));
};
export const useDesignActions = () => {
  return useStore(useDesignStore(), useShallow(_temp4));
};
export const useWippenSupportSpringsDesign = () => {
  return useStore(useDesignStore(), useShallow(_temp5));
};
function _temp(state) {
  return {
    frontWeightDesignComputedBalanceWeightTarget: state.frontWeightDesignComputedBalanceWeightTarget,
    frontWeightDesignMode: state.frontWeightDesignMode,
    frontWeightDesignStandardTarget: state.frontWeightDesignStandardTarget,
    frontWeightDesignTarget: state.frontWeightDesignTarget
  };
}
function _temp2(state) {
  return {
    strikeWeightDesignComputedBalanceWeightTarget: state.strikeWeightDesignComputedBalanceWeightTarget,
    strikeWeightDesignMode: state.strikeWeightDesignMode,
    strikeWeightDesignSmoothTarget: state.strikeWeightDesignLatestSmoothTarget,
    strikeWeightDesignStandarTarget: state.strikeWeightDesignLatestStandarTarget,
    strikeWeightDesignTarget: state.strikeWeightDesignTarget
  };
}
function _temp3(state) {
  return {
    strikeWeightRatioDesignLatestFixedTarget: state.strikeWeightRatioDesignLatestFixedTarget,
    strikeWeightRatioDesignLatestSmoothTarget: state.strikeWeightRatioDesignLatestSmoothTarget,
    strikeWeightRatioDesignMode: state.strikeWeightRatioDesignMode,
    strikeWeightRatioDesignTarget: state.strikeWeightRatioDesignTarget
  };
}
function _temp4(state) {
  return {
    reset: state.reset,
    updateFrontWeightDesign: state.updateFrontWeightDesign,
    updateStrikeWeightDesign: state.updateStrikeWeightDesign,
    updateStrikeWeightRatioDesign: state.updateStrikeWeightRatioDesign,
    updateWippenSupportSpringsDesign: state.updateWippenSupportSpringsDesign
  };
}
function _temp5(state) {
  return {
    wippenSupportSpringsDesignLatestNumberOfSprings: state.wippenSupportSpringsDesignLatestNumberOfSprings,
    wippenSupportSpringsDesignLatestSpringBalanceWeight: state.wippenSupportSpringsDesignLatestSpringBalanceWeight,
    wippenSupportSpringsDesignLatestTensionDropIndex: state.wippenSupportSpringsDesignLatestTensionDropIndex,
    wippenSupportSpringsDesignMode: state.wippenSupportSpringsDesignMode,
    wippenSupportSpringsDesignTarget: state.wippenSupportSpringsDesignTarget
  };
}